
import { useState, useEffect } from "react";
import { intl } from "LocaleWrapper";
import { Table } from "@cofano/cck";
import DataFormat from "components/utils/DataFormat";
import { TableId, useFrontendTable } from "components/table";
import { CollapseButton } from "./Declarations";
import { Collapse } from "@mui/material";
import { usePermission } from "utils";
import { VoyageLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";
import VoyageUpdateScheduleDialog from "view/voyages/components/dialogs/VoyageUpdateScheduleDialog";

const SalesCharters = ({ charter, loading, onRowClick, extraColumns = [], refetch, purchaseChartersRequestId }) => {

	const { isReadOnly } = usePermission();

	const [ collapsed, collapse ] = useState(false);
	const [ dialog, setDialog ] = useState({
		open: false,
		voyageIds: [],
	});

	const { tableProps, refetch: tableRefetch, clearSelection } = useFrontendTable({
		id: TableId.DeclarationSalesCharters,
		content: charter.voyages,
		paginationEnabled: false,
	});

	useEffect(() => {
		tableRefetch(0);
	}, [ loading ]);

	const selectionEnabled = !charter?.declarations?.[0]?.closedOn && !isReadOnly;

	const getLocation = (location) => {
		const port = location?.locationGroups?.find(l => l.type === "PORT");
		const loc = port || location;
		return loc?.description || loc?.displayCode || loc?.code;
	};

	return (
		<div>
			<CollapseButton collapsed={collapsed} onClick={() => collapse(!collapsed)} vertical />

			<Collapse in={!collapsed} timeout="auto" unmountOnExit>
				<Table
					{...tableProps}
					key={purchaseChartersRequestId}
					selectSettings={{
						selectionEnabled: selectionEnabled,
						isFullSelectEnabled: false,
					}}
					contentLoading={loading}
					onRowClick={onRowClick}
					paginationSettings={undefined}
					columns={[ {
						id: "voyageNumber",
						title: intl.formatMessage({ id: "VOYAGE" }),
						width: 200,
						render: row => <VoyageLink voyage={row} />,
						sortable: true,
					}, {
						id: "administrativeTimestamp",
						title: intl.formatMessage({ id: "VOYAGE_ADMINISTRATIVE_START" }),
						render: (row, field) => <FormattedDate date={field} format="ll" />,
					}, {
						id: "start",
						title: intl.formatMessage({ id: "VOYAGE_START" }),
						render: (row, field) => <FormattedDate date={field} format="ll" />,
						sortable: true,
					}, {
						id: "from",
						title: intl.formatMessage({ id: "FROM" }),
						render: (row, field) => getLocation(field),
					}, {
						id: "end",
						title: intl.formatMessage({ id: "VOYAGE_END" }),
						render: (row, field) => <FormattedDate date={field} format="ll" />,
						sortable: true,
					}, {
						id: "to",
						title: intl.formatMessage({ id: "TO" }),
						render: (row, field) => getLocation(field),
					}, {
						id: "voyageMetrics.loadedTeu",
						title: intl.formatMessage({ id: "TEU" }),
						render: (row, field) => field || 0,
					}, {
						id: "voyageMetrics.loadedTeuPercentage",
						title: `${intl.formatMessage({ id: "TEU" })} (%)`,
						render: (row, field) => DataFormat.percentage(field * 100, 0),
					}, {
						id: "voyageMetrics.loadedTon",
						title: intl.formatMessage({ id: "MTON" }),
						render: (row, field) => DataFormat.volumeTon(field || 0),
					}, {
						id: "voyageMetrics.loadedTonPercentage",
						title: `${intl.formatMessage({ id: "MTON" })} (%)`,
						render: (row, field) => DataFormat.percentage(field * 100, 0),
					}, {
						id: "revenue",
						title: intl.formatMessage({ id: "REVENUE" }),
						render: (row, field) => DataFormat.price(field),
					},
					{
						id: "revenuePerTeu",
						title: `${intl.formatMessage({ id: "REVENUE" })} / TEU`,
						render: (row, field) => DataFormat.price(row.revenue / (row.voyageMetrics?.loadedTeu || 1)),
					},
					{
						id: "revenuePerTon",
						title: `${intl.formatMessage({ id: "REVENUE" })} / ${intl.formatMessage({ id: "MTON" })}`,
						render: (row, field) => DataFormat.price(row.revenue / (row.voyageMetrics?.loadedTon || 1)),
					},
					{
						id: "schedule.schedule",
						title: intl.formatMessage({ id: "VOYAGE_SCHEDULE" }),
					}, ...extraColumns ]}
					actions={selectionEnabled && [ {
						title: intl.formatMessage({ id: "CHANGE_VOYAGE_SCHEDULE" }),
						onClick: (selection) => {
							setDialog({
								open: true,
								voyageIds: selection.map(s => s.id),
							});
						},
					} ]} />
			</Collapse>

			<VoyageUpdateScheduleDialog
				open={dialog.open}
				onClose={() => setDialog({ open: false, voyageIds: [] })}
				voyageIds={dialog.voyageIds}
				refetch={() => {
					clearSelection();
					refetch?.();
				}} />
		</div>
	);
};

export default SalesCharters;
